import * as React from 'react';
import { useRef, useState } from 'react';
import type { Placement } from '@floating-ui/react';
import {
  arrow,
  autoUpdate,
  flip,
  FloatingArrow,
  FloatingPortal,
  offset,
  shift,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useRole
} from '@floating-ui/react';

import styles from './Tooltip.module.scss';
import classNames from 'classnames';

interface TooltipProps {
  title: string;
  children: React.ReactNode;
  placement?: Placement;
  className?: string;
  // could be used if for some reason anchor doesn't have a ref(f.e. it's a functional component without forwardRef)
  useAnchorWrapper?: boolean;
}

export function Tooltip(props: TooltipProps) {
  const { children, placement = 'top', title, useAnchorWrapper, className } = props;

  const [open, setOpen] = useState(false);

  const arrowRef = useRef(null);

  const { context, floatingStyles, refs } = useFloating({
    placement,
    open,
    onOpenChange: setOpen,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(8),
      flip({
        crossAxis: placement.includes('-'),
        fallbackAxisSideDirection: 'start',
        padding: 5
      }),
      shift({ padding: 5 }),
      arrow({
        element: arrowRef
      })
    ]
  });

  const hover = useHover(context, {
    move: false
  });
  const focus = useFocus(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: 'tooltip' });

  const { getReferenceProps, getFloatingProps } = useInteractions([hover, focus, dismiss, role]);

  if (!React.isValidElement(children)) {
    throw new Error('Tooltip must have a single valid child');
  }

  const anchor = useAnchorWrapper ? (
    <div {...getReferenceProps()} ref={refs.setReference}>
      {children}
    </div>
  ) : (
    React.cloneElement(
      children,
      getReferenceProps({
        ref: refs.setReference,
        ...children.props,
        'data-state': context.open ? 'open' : 'closed'
      })
    )
  );

  return (
    <>
      {anchor}
      {open && (
        <FloatingPortal>
          <div
            className={classNames(styles.tooltip, className)}
            ref={refs.setFloating}
            style={{
              ...floatingStyles
            }}
            {...getFloatingProps()}
          >
            {title}
            <FloatingArrow ref={arrowRef} context={context} fill={'black'} width={8} height={4} />
          </div>
        </FloatingPortal>
      )}
    </>
  );
}
